import { gsap } from 'gsap/all';

import { KHPoint } from '../../../../LearningWorlds/pixi-puzzle-framework/PuzzleEngine/KHPoint';
import { SetDraggable } from '../../../mixins/DraggableMixin';

export type TileData = {
  imageUrl: string;
  value: string;
};

export enum TileType {
  ONE = '1',
  MINUS_ONE = '-1',
  X = 'x',
  MINUS_X = '-x',
  X2 = 'x^2',
  MINUS_X2 = '-x^2'
}

export interface TilePosition {
  left: number;
  top: number;
  width: number;
  height: number;
}

export const tileDataMap: Record<TileType, TileData> = {
  [TileType.ONE]: {
    imageUrl: 'images/tiles/1.svg',
    value: '1'
  },
  [TileType.MINUS_ONE]: {
    imageUrl: 'images/tiles/-1.svg',
    value: '-1'
  },
  [TileType.X]: {
    imageUrl: 'images/tiles/x.svg',
    value: 'x'
  },
  [TileType.MINUS_X]: {
    imageUrl: 'images/tiles/-x.svg',
    value: '-x'
  },
  [TileType.X2]: {
    imageUrl: 'images/tiles/x2.svg',
    value: 'x^2'
  },
  [TileType.MINUS_X2]: {
    imageUrl: 'images/tiles/-x2.svg',
    value: '-x^2'
  }
};

export class AlgebaTileDraggable {
  public draggableDiv: HTMLDivElement;
  public tileType: TileType;
  public hasIntersection: boolean;

  private image: HTMLImageElement;
  private angle = 0;
  private onDrag: (pos: KHPoint) => void;
  private onDragStop: () => void;
  private parent: HTMLDivElement;
  private removeDraggable: () => void;
  private rotationAngle = 0;

  public constructor(parent: HTMLDivElement, tileType: TileType, startPosition: KHPoint, onDrag: (pos: KHPoint) => void, onDragStop: () => void) {
    this.tileType = tileType;
    this.parent = parent;

    this.onDragStop = onDragStop;
    this.draggableDiv = document.createElement('div');
    this.draggableDiv.style.position = 'absolute';
    this.draggableDiv.style.cursor = 'move';
    this.draggableDiv.style.userSelect = 'none';
    this.draggableDiv.style.outline = 'none';
    this.draggableDiv.style.transition = 'transform 0.5s ease-in';
    this.draggableDiv.style.left = startPosition.x.toString() + 'px';
    this.draggableDiv.style.top = startPosition.y.toString() + 'px';

    this.image = new Image();
    this.image.src = tileDataMap[tileType].imageUrl;
    this.image.draggable = false;

    this.image.onload = () => {
      const width = this.image.width;
      const height = this.image.height;
      this.draggableDiv.style.width = width.toString() + 'px';
      this.draggableDiv.style.height = height.toString() + 'px';
      this.draggableDiv.appendChild(this.image);
      parent.appendChild(this.draggableDiv);
    };

    this.onDrag = onDrag;

    this.removeDraggable = SetDraggable(this.draggableDiv, undefined, this.onDragInternal.bind(this), this.onDragEnd.bind(this));
  }

  private onDragInternal(pos: KHPoint): void {
    this.onDrag(pos);
    this.draggableDiv.style.zIndex = '10';
  }

  private onDragEnd(): void {
    this.draggableDiv.style.zIndex = '0';
    this.parent.appendChild(this.draggableDiv); // Bring to front
    this.onDragStop();
  }

  public getRect(): DOMRect {
    return this.draggableDiv.getBoundingClientRect();
  }

  public setIntersecting(intersecting: boolean): void {
    this.hasIntersection = intersecting;
    this.draggableDiv.style.opacity = intersecting ? '0.5' : '1';
  }

  private isTileOpposite(other: AlgebaTileDraggable): boolean {
    const ttNormal = this.tileType.toString();
    const otherType = other.tileType.toString();
    const ttNegated = `-${ttNormal}`;
    const otherNegated = `-${otherType}`;

    return (ttNegated === otherType || otherNegated === ttNormal);
  }

  /**
   * Returns true if the tiles overlap and also are of opposite values
   * @param other - The other tile
   * @returns true if the tiles can intersect with the above logic
   */
  public canIntersect(other: AlgebaTileDraggable): boolean {
    if (!this.isTileOpposite(other)) {
      return false;
    }

    const rect1 = this.getRect();
    const rect2 = other.getRect();

    return !(
      rect2.left > rect1.left + rect1.width
      || rect2.left + rect2.width < rect1.left
      || rect2.top > rect1.top + rect1.height
      || rect2.top + rect2.height < rect1.top
    );
  }

  public isOutsideBounds(containerBounds: TilePosition): boolean {
    const pos = this.getRect();
    return (
      pos.left < 0
      || pos.top < 0
      || pos.left + pos.width > containerBounds.width
      || pos.top + pos.height > containerBounds.height
    );
  }

  public delete(animate = false): void {
    if (animate) {
      this.animateDelete();
    } else {
      this.removeDraggable();
      this.draggableDiv.remove();
    }
  }

  private animateDelete(): void {
    this.removeDraggable();

    gsap.to(this.draggableDiv, {
      duration: 0.6,
      opacity: 0,
      onComplete: () => {
        this.delete();
      }
    });
  }

  public rotate(): void {
    this.rotationAngle = this.rotationAngle + 90; // do not mod 360
    this.draggableDiv.style.transform = 'rotate(' + this.rotationAngle.toString() + 'deg)';
  }

  public getPosition(): KHPoint {
    return { x: parseFloat(this.draggableDiv.style.left), y: parseFloat(this.draggableDiv.style.top) };
  }

  public changeSign(): void {
    let newTypeString = '';
    if (this.tileType.toString().startsWith('-')) {
      newTypeString = this.tileType.toString().slice(1);
    } else {
      newTypeString = `-${this.tileType.toString()}`;
    }

    const newType = this.enumFromString(newTypeString);

    if (newType) {
      console.log(newType);
      this.tileType = newType;
    }

    console.log(this.tileType);

    this.image.src = tileDataMap[this.tileType].imageUrl;
  }

  private enumFromString(str: string): TileType | undefined {
    const values = Object.values(TileType);
    console.log(values);

    if (values.includes(str as TileType)) {
      return str as TileType;
    }

    return undefined;
  }
}
